import { useState, useEffect, useRef } from "react";
import {
  calculateDefaultMargin,
  priceFromMargin,
  marginFromPrice,
  rev4Avg,
  totalReferenceCents,
  marginAbsCents as marginAbsCentsFn,
  Rev4Item,
  ReferenceType,
} from "../helpers/calculateMargin";

/**
 * Controls whether the user is piloting by margin or by final price.
 * - "margin": changing cost recalculates price, preserving margin.
 * - "price":  changing cost recalculates margin, preserving price.
 */
export type MarginIntent = "margin" | "price";

export interface UseMarginCalculatorResult {
  /** @deprecated F2-VR: alias of `referenceCents`. Use referenceCents instead. */
  costCents: number;
  /** Total reference value in cents (cost or rev3, depending on referenceType). */
  referenceCents: number;
  /** Active reference type used for computations. */
  referenceType: ReferenceType;
  defaultMargin: number;
  margin: number;
  setMargin: (m: number) => void;
  priceCents: number;
  setPrice: (p: number) => void;
  intent: MarginIntent;
  setIntent: (i: MarginIntent) => void;
  rev4Avg: number;
  isInvalid: boolean;
  /** Absolute margin in cents = priceCents − referenceCents. May be negative. */
  marginAbsCents: number;
}

export function useMarginCalculator(
  items: Rev4Item[],
  referenceType: ReferenceType = "COST",
): UseMarginCalculatorResult {
  const referenceCents = totalReferenceCents(items, referenceType);
  const defaultMargin = calculateDefaultMargin(referenceCents);

  const [margin, setMarginState] = useState<number>(defaultMargin);
  const [priceCents, setPriceCents] = useState<number>(
    priceFromMargin(referenceCents, defaultMargin),
  );
  const [intent, setIntent] = useState<MarginIntent>("margin");

  // Track the previous referenceCents to detect threshold crossings.
  const prevRefRef = useRef<number>(referenceCents);
  const THRESHOLD = 150_000;

  useEffect(() => {
    const prev = prevRefRef.current;
    prevRefRef.current = referenceCents;

    const crossedThreshold =
      (prev < THRESHOLD && referenceCents >= THRESHOLD) ||
      (prev >= THRESHOLD && referenceCents < THRESHOLD);

    if (crossedThreshold) {
      // Threshold crossed → reset to new default regardless of intent.
      const dm = calculateDefaultMargin(referenceCents);
      setMarginState(dm);
      setPriceCents(priceFromMargin(referenceCents, dm));
      return;
    }

    // No threshold crossing — respect intent.
    if (intent === "margin") {
      // Pilot by margin: recalculate price to keep margin stable.
      setPriceCents(priceFromMargin(referenceCents, margin));
    } else {
      // Pilot by price: recalculate margin to keep price stable.
      setMarginState(marginFromPrice(referenceCents, priceCents));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceCents]);

  const setMargin = (m: number) => {
    setIntent("margin");
    setMarginState(m);
    setPriceCents(priceFromMargin(referenceCents, m));
  };

  const setPrice = (p: number) => {
    setIntent("price");
    setPriceCents(p);
    setMarginState(marginFromPrice(referenceCents, p));
  };

  const isInvalid = priceCents < referenceCents;

  return {
    costCents: referenceCents,
    referenceCents,
    referenceType,
    defaultMargin,
    margin,
    setMargin,
    priceCents,
    setPrice,
    intent,
    setIntent,
    rev4Avg: rev4Avg(items),
    isInvalid,
    marginAbsCents: marginAbsCentsFn(priceCents, referenceCents),
  };
}
